import { IAgTechAppContext } from "../app/AgTechAppContext";
import { AgTechDataAction, AgTechDataActionGetDataActionProps, AgTechDataActionValidationProps, getActionLoggingContext } from "../data/actions/AgTechDataActions";
import { AgTechFormControlIndex } from "../forms/AgTechFormControls";
import { AgTechFormState } from "../forms/AgTechFormState";
import { executeWithMinimumDelay } from "../utilities/AgTechUtilities";
import { Dispatch } from "redux";

export type AgTechFormDataActionProps<TFormData, TActionData, TResponseData, TContextData = TActionData> = {
    appContext: IAgTechAppContext,
    dispatch: Dispatch,
    initialFormData: TFormData,
    getActionData: AgTechFormDataActionProjection<TFormData, TContextData, TActionData>,
    formDataAction: AgTechDataAction<TActionData, TResponseData, TContextData>,
    formDataRef: React.MutableRefObject<TFormData>,
    formControlsRef: React.MutableRefObject<AgTechFormControlIndex<TFormData>>,
    setFormState: (formState: AgTechFormState) => void
}

export type AgTechFormDataActionProjection<TFormData, TContextData, TActionData> =
    (formData: TFormData, context: TContextData) => TActionData;

export const useFormDataAction = <TFormData, TActionData, TResponseData, TContextData = TActionData>(
    formDataActionProps: AgTechFormDataActionProps<TFormData, TActionData, TResponseData, TContextData>
): AgTechDataAction<TActionData, TResponseData, TContextData> => {
    return {
        ...formDataActionProps.formDataAction,
        name: formDataActionProps.formDataAction.name,
        getConfiguration: formDataActionProps.formDataAction.getConfiguration,
        confirm: formDataActionProps.formDataAction.confirm,
        onSuccess: formDataActionProps.formDataAction.onSuccess,
        onFailure: formDataActionProps.formDataAction.onFailure,
        validate: async (props) => await validateFormDataAction(formDataActionProps, props),
        action: async (props) => {
            return await executeFormDataAction(formDataActionProps, props);
        }
    }
}

const validateFormDataAction = async <TFormData, TActionData, TResponseData, TContextData = TActionData>(
    props: AgTechFormDataActionProps<TFormData, TActionData, TResponseData, TContextData>,
    validationProps: AgTechDataActionValidationProps<TActionData, TContextData>,
) => {
    const warningMessages: string[] = [];

    Object.values(props.formControlsRef.current).forEach(field => {
        let fieldWarningMessage = field.validateOnSubmit(props.formDataRef.current);

        if (fieldWarningMessage) {
            warningMessages.push(fieldWarningMessage);
        }
    });

    if (warningMessages && warningMessages.length > 0) {
        warningMessages.forEach(warning => {
            validationProps.validation.failWithWarning(warning);
        });
    }
    else if (props.formDataAction.validate) {
        if (props.formDataAction.validate) {
            await props.formDataAction.validate({
                ...validationProps,
            });
        }
    }
}

const executeFormDataAction = async <TFormData, TActionData, TResponseData, TContextData = TActionData>(
    props: AgTechFormDataActionProps<TFormData, TActionData, TResponseData, TContextData>,
    actionProps: AgTechDataActionGetDataActionProps<TActionData, TContextData>
) => {
    props.setFormState(AgTechFormState.Submitting);

    let executionProps = {
        originalEntity: props.getActionData(props.initialFormData, actionProps.context),
        submittedEntity: props.getActionData(props.formDataRef.current, actionProps.context)
    };

    const actionConfiguration = props.formDataAction.getConfiguration
        ? props.formDataAction.getConfiguration(executionProps)
        : undefined;

    return await executeWithMinimumDelay({
        action: async () => {
            const result = await props.formDataAction.action({
                ...executionProps,
                ...actionProps,
                actionLoggingContext: getActionLoggingContext({
                    ...executionProps,
                    dataAction: props.formDataAction
                })
            });

            if (result.success) {
                props.setFormState(AgTechFormState.Successful);

                if (actionConfiguration?.actionConfirmationMessage) {
                    props.appContext.messaging.showSuccess({
                        header: actionConfiguration.actionConfirmationMessage
                    });
                }
            }
            else {
                props.setFormState(AgTechFormState.Failed);
            }

            return result;
        },
        minimumDelay: 500
    });
}